import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { getHolderbyTime } from "../../../api/network";
import { useEffect, useState } from "react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

export const options = {
  responsive: true,
  maintainAspectRatio: false, // Allows the container to control height
  interaction: {
    mode: "index",
    intersect: false, // Better UX: hover anywhere on the vertical line
  },
  plugins: {
    legend: {
      position: "top",
      align: "end", // Align legend to the right
      labels: {
        usePointStyle: true, // Circular legend icons look cleaner
        pointStyle: "circle",
        padding: 15,
        font: { size: 12, family: "'Poppins', sans-serif" },
      },
    },
    tooltip: {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      titleColor: "#1e293b",
      bodyColor: "#1e293b",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      padding: 12,
      boxPadding: 6,
      usePointStyle: true,
      titleFont: {
        family: "'Poppins', sans-serif",
        size: 12,
        weight: "600",
      },
      bodyFont: {
        family: "'Poppins', sans-serif",
        size: 12,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false }, // Hide vertical grid lines for a cleaner look
      ticks: {
        color: "#94a3b8",
        maxRotation: 0,
        font: { size: 12, family: "'Poppins', sans-serif" },
      },
    },
    y: {
      beginAtZero: true,
      border: { dash: [4, 4] }, // Dashed horizontal lines
      grid: { color: "#f1f5f9" },
      ticks: {
        color: "#94a3b8",
        font: { size: 12, family: "'Poppins', sans-serif" },
      },
    },
  },
  elements: {
    line: {
      tension: 0.4, // Adds smooth Bezier curves
      borderWidth: 2,
      capBezierPoints: true,
    },
    point: {
      radius: 0, // Hide points by default
      hoverRadius: 4, // Show on hover for "pop" effect
    },
  },
};

export default function HolderConnections() {
  const [groupBy, setGroupBy] = useState("week");
  const [chartData, setChartData] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: "2024-12-01",
    end: "2026-04-27",
  });

  const fetchChart = async () => {
    const networkId = localStorage.getItem("networkId");
    if (!networkId) return;

    try {
      const data = await getHolderbyTime(
        dateRange.start,
        dateRange.end,
        groupBy,
        networkId,
      );
      // Ensure we set the array (check if your API returns .result or .data)
      setChartData(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchChart();
  }, [dateRange, groupBy]);

  const shouldShowYear =
    new Date(dateRange.start).getFullYear() !==
    new Date(dateRange.end).getFullYear();

  const chartConfig = {
    labels: chartData.map((item) => {
      const date = new Date(item.date.split("T")[0]);

      return date.toLocaleDateString("en-IN", {
        ...(groupBy === "month" && { month: "short" }),
        ...(groupBy === "day" && { day: "2-digit", month: "short" }),
        ...(groupBy === "week" && { day: "2-digit", month: "short" }),
        ...(shouldShowYear && { year: "numeric" }),
      });
    }),

    datasets: [
      {
        label: "Holder Connections",
        data: chartData.map((item) => Number(item.count)),
        borderColor: "rgb(99, 102, 241)",
        backgroundColor: (ctx) => {
          const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(139,92,246,0.2)");
          gradient.addColorStop(1, "rgba(139,92,246,0)");
          return gradient;
        },
        fill: true,
      },
    ],
  };
  return (
    <div
      style={{
        height: "400px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        borderRadius: "12px",
      }}
    >
      <div className="chart-controls d-flex gap-3 align-items-end">
        <div className="date-filter">
          <div className="date-field">
            <label>From</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  start: e.target.value,
                }))
              }
            />
          </div>

          <div className="date-field">
            <label>To</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  end: e.target.value,
                }))
              }
            />
          </div>
        </div>

        <div className="btn-group ">
          <button
            type="button"
            className="btn btn-sm btn-primary dropdown-toggle rounded-3"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {groupBy === "day"
              ? "Daily"
              : groupBy === "week"
                ? "Weekly"
                : "Monthly"}
          </button>

          <ul className="dropdown-menu">
            <li>
              <button
                className={`dropdown-item ${groupBy === "day" ? "active" : ""}`}
                onClick={() => setGroupBy("day")}
              >
                Daily
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${groupBy === "week" ? "active" : ""}`}
                onClick={() => setGroupBy("week")}
              >
                Weekly
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${groupBy === "month" ? "active" : ""}`}
                onClick={() => setGroupBy("month")}
              >
                Monthly
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
        <Line options={options} data={chartConfig} />
      </div>
    </div>
  );
}
